/* Lux PDP: variant-scoped gallery.
   Slides carry data-lux-media-scope (see snippets/lux-media-scope.liquid) when their media
   belongs to one option value. On load and on every variant change, only slides matching the
   selected variant (plus unscoped slides) stay in the DOM, so Swiper's counters stay truthful.
   If a variant would leave zero slides, everything is shown instead. */
(() => {
  const norm = (s) => (s || "").trim().toLowerCase();

  const applyScope = (selectedValues) => {
    const selected = (selectedValues || []).map(norm).filter(Boolean);
    if (!selected.length) return;

    document.querySelectorAll("gallery-section").forEach((gallery) => {
      if (!gallery.querySelector("[data-lux-media-scope]") && !gallery.luxFiltered) return;

      gallery.querySelectorAll("[data-swiper-container]").forEach((container) => {
        if (!container.luxAllSlides) {
          container.luxAllSlides = Array.from(container.children).filter((el) =>
            el.matches("[data-swiper-slide]")
          );
        }
        const all = container.luxAllSlides;
        let allowed = all.filter((el) => {
          const scope = el.dataset.luxMediaScope;
          return !scope || selected.includes(norm(scope));
        });
        if (allowed.length === 0) allowed = all;

        const current = Array.from(container.children).filter((el) =>
          el.matches("[data-swiper-slide]")
        );
        const same =
          current.length === allowed.length && current.every((el, i) => el === allowed[i]);
        if (same) return;

        current.forEach((el) => el.remove());
        allowed.forEach((el) => container.appendChild(el));
        gallery.luxFiltered = true;

        /* Swiper attaches its instance to the .swiper ROOT, not the wrapper; without
           this the update/slideTo/height sync below never ran (observer is off, so
           Swiper's slide grid goes stale against the re-appended DOM). */
        const sw = (container.closest(".swiper") || container).swiper;
        if (sw && sw.update) {
          sw.update();
          if (sw.slideTo) sw.slideTo(0, 0);
          /* Mobile gallery runs autoHeight: measuring right after re-append reads
             slides whose lazy images have no real layout yet, freezing the gallery
             small. Re-measure next frame and whenever a late image finishes. */
          if (sw.params && sw.params.autoHeight && sw.updateAutoHeight) {
            requestAnimationFrame(() => sw.updateAutoHeight(0));
            container.querySelectorAll("img").forEach((img) => {
              if (!img.complete)
                img.addEventListener("load", () => sw.updateAutoHeight(0), { once: true });
            });
          }
        }
      });
    });
  };

  /* Delivery estimate: order day never counts, processing starts the next working day,
     then +min/+max working days (data-lux-min/max from the Liquid; absent = the stock
     2/7 range, equal = made-to-order single promised date). Working = not Sat/Sun and
     not a Cyprus public holiday (fixed dates + Orthodox-Easter movables: Green Monday,
     Good Friday, Easter Monday and Tuesday, Whit Monday). Static locale text stays as
     the no-JS fallback. */
  const luxDelivery = () => {
    const el = document.querySelector("[data-lux-delivery]");
    if (!el) return;
    const pad = (n) => String(n).padStart(2, "0");
    const key = (d) => d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
    const orthodoxEaster = (y) => {
      const a = y % 4, b = y % 7, c = y % 19;
      const d = (19 * c + 15) % 30;
      const e = (2 * a + 4 * b - d + 34) % 7;
      const dt = new Date(y, Math.floor((d + e + 114) / 31) - 1, ((d + e + 114) % 31) + 1);
      dt.setDate(dt.getDate() + 13); // Julian to Gregorian, valid 1900-2099
      return dt;
    };
    const holidays = new Set();
    const year = new Date().getFullYear();
    [year, year + 1].forEach((y) => {
      ["01-01", "01-06", "03-25", "04-01", "05-01", "08-15", "10-01", "10-28", "12-25", "12-26"]
        .forEach((md) => holidays.add(y + "-" + md));
      const easter = orthodoxEaster(y);
      [-48, -2, 1, 2, 50].forEach((off) => {
        const d = new Date(easter);
        d.setDate(d.getDate() + off);
        holidays.add(key(d));
      });
    });
    const isWork = (d) => d.getDay() !== 0 && d.getDay() !== 6 && !holidays.has(key(d));
    const nextWork = (d) => { const x = new Date(d); while (!isWork(x)) x.setDate(x.getDate() + 1); return x; };
    const addWork = (d, n) => { const x = new Date(d); while (n > 0) { x.setDate(x.getDate() + 1); if (isWork(x)) n--; } return x; };
    const start = nextWork(new Date());
    /* words + date locale come from the Liquid that rendered the element, so the
       line reads in the shopper's language. The Greek defaults are the fallback
       for a stale cached template that predates the data-* attributes. */
    const fmt = new Intl.DateTimeFormat(el.dataset.luxLocale || "el-GR", { weekday: "short", day: "numeric", month: "short" });
    const prefix = el.dataset.luxPrefix || "Σε απόθεμα · παράδοση";
    const to = el.dataset.luxTo || "έως";
    const min = parseInt(el.dataset.luxMin, 10) || 2;
    const max = parseInt(el.dataset.luxMax, 10) || 7;
    /* Dates in bold (Tsoulfa 2026-07-11). All parts come from our own Liquid
       data-* attributes and Intl, safe for innerHTML. */
    const body = min === max
      ? prefix + " " + to + " <strong>" + fmt.format(addWork(start, max)) + "</strong>"
      : prefix + " <strong>" + fmt.format(addWork(start, min)) + "</strong> " + to + " <strong>" + fmt.format(addWork(start, max)) + "</strong>";
    /* ONE span so .lux-stock (a flex row: dot + text) keeps the whole sentence as a
       single flex child. Without it each <strong>/text node is its own flex item and
       a narrow phone column shrank the loose "έως" below its width, breaking it to
       "έω/ς". As one child the line wraps like normal prose. */
    el.innerHTML = '<span class="lux-stock__txt">' + body + "</span>";
  };

  /* Fabric picker (made-to-order family): CSS owns family->colour visibility;
     this engine clears cross-family colours, fills the localized name lines and
     the preview chip, and gates the buy form until both choices exist
     (docs/plans/2026-07-11-fabric-picker-design.md). */
  const luxFabric = () => {
    const box = document.querySelector("[data-lux-fabric]");
    if (!box) return;
    const fams = [...box.querySelectorAll(".lux-fabric__fam")];
    const cols = [...box.querySelectorAll(".lux-fabric__colors input")];
    const famName = box.querySelector("[data-lux-fam-name]");
    const colName = box.querySelector("[data-lux-col-name]");
    const preview = box.querySelector("[data-lux-fabric-preview]");
    const pImg = preview && preview.querySelector("[data-lux-preview-img]");
    const pCode = preview && preview.querySelector("[data-lux-preview-code]");
    const chooseText = famName ? famName.textContent : "";
    /* Server renders the colour label as «διάλεξε πρώτα ύφασμα» (no swatches exist
       until a family is picked); once a family is in, the plain «διάλεξε» takes over. */
    const colStartText = colName ? colName.textContent : "";
    const selFam = () => fams.find((f) => f.checked);
    const selCol = () => cols.find((c) => c.checked);
    const sync = () => {
      const f = selFam();
      const c = selCol();
      if (famName) famName.textContent = f ? f.dataset.label || f.value : chooseText;
      if (colName) colName.textContent = c ? c.value : f ? chooseText : colStartText;
      if (preview) {
        preview.hidden = !c;
        if (c && pImg && pCode) {
          const img = c.parentElement.querySelector("img");
          if (img) { pImg.src = img.currentSrc || img.src; pImg.alt = c.value; }
          pCode.textContent = c.value;
        }
      }
    };
    box.addEventListener("change", (e) => {
      if (e.target.classList.contains("lux-fabric__fam")) {
        const c = selCol();
        if (c && c.dataset.fam !== e.target.dataset.fam) c.checked = false;
      }
      sync();
    });
    sync();
  };

  /* Corner picker (corner sofas): the name line mirrors the chosen side in the
     shopper's language; completeness is enforced by luxChoiceGate below. */
  const luxCorner = () => {
    const box = document.querySelector("[data-lux-choice] [data-lux-corner-name]");
    if (!box) return;
    const root = box.closest("[data-lux-choice]");
    const chooseText = box.textContent;
    const sync = () => {
      const c = [...root.querySelectorAll('input[type=radio]')].find((i) => i.checked);
      box.textContent = c ? c.dataset.label || c.value : chooseText;
    };
    root.addEventListener("change", sync);
    sync();
  };

  /* ONE submit gate for every required-choice picker on the PDP (fabric, corner):
     inside each [data-lux-choice] root, EVERY distinct radio-name group must have
     a checked input before the buy form may submit. Capture phase beats Wonder's
     product-form AJAX handler; the sticky buy button submits this same form. */
  const luxChoiceGate = () => {
    const boxes = [...document.querySelectorAll("[data-lux-choice]")];
    if (!boxes.length) return;
    const form = document.getElementById("product-form-" + boxes[0].dataset.sectionId);
    if (!form) return;
    /* A GROUP is one radio name inside the box (fabric box = Ύφασμα + Χρώμα, corner
       box = Γωνία). Refusing the add is not enough: the shopper must see WHICH choice
       is still open, so each unanswered group is named, not just the box. */
    const missingNames = (box) => {
      const names = [...new Set([...box.querySelectorAll('input[type=radio]')].map((i) => i.name))];
      return names.filter((n) => !box.querySelector('input[name="' + CSS.escape(n) + '"]:checked'));
    };
    const labels = (box) => [...box.querySelectorAll("[data-lux-req-for]")];
    /* Red star on every group still open, gone the moment that group is answered;
       the wording stays in the role=alert line, which mirrors the same state. */
    const paint = (box) => {
      const missing = missingNames(box);
      labels(box).forEach((label) => {
        const star = label.querySelector("[data-lux-req]");
        if (star) star.hidden = missing.indexOf(label.dataset.luxReqFor) === -1;
      });
      const err = box.querySelector("[data-lux-choice-error]");
      if (err) err.hidden = !missing.length;
      return missing;
    };
    /* Silent until the shopper actually tries to buy: stars on a fresh page would
       read as a form to fill in, not as a product to want. */
    let armed = false;
    form.addEventListener("submit", (e) => {
      const blocked = boxes.filter((b) => missingNames(b).length);
      if (!blocked.length) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      armed = true;
      boxes.forEach(paint);
      /* Land on the first open group itself, not the top of its box: on a phone the
         box can be taller than the screen and the missing row scrolls off. */
      const first = labels(blocked[0]).find((l) => {
        const star = l.querySelector("[data-lux-req]");
        return star && !star.hidden;
      }) || blocked[0];
      const reduce = window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches;
      first.scrollIntoView({ block: "center", behavior: reduce ? "auto" : "smooth" });
    }, true);
    boxes.forEach((b) => b.addEventListener("change", () => { if (armed) paint(b); }));
  };

  /* Sticky-header section tabs (spec 2026-07-11): links whose target is missing
     hide themselves; clicks smooth-scroll (reduced-motion honored); the section
     currently under the bar carries aria-current, which the CSS underlines. */
  const luxStickyTabs = () => {
    const nav = document.querySelector(".lux-sticky-tabs");
    if (!nav) return;
    const links = [...nav.querySelectorAll('a[href^="#"]')].filter((a) => {
      const target = document.getElementById(a.hash.slice(1));
      if (!target) a.hidden = true;
      return !!target;
    });
    if (!links.length) { nav.hidden = true; return; }
    const targets = links.map((a) => document.getElementById(a.hash.slice(1)));
    const bar = nav.closest("sticky-buy-button");
    const barOffset = () => Math.ceil(bar?.getBoundingClientRect().height || 68) + 16;
    // Translated titles and payment details can wrap. Keep destination headings
    // below the complete bar instead of assuming a fixed one-line height.
    const syncOffset = () => document.documentElement.style.setProperty("--lux-sticky-offset", barOffset() + "px");
    if (bar) new ResizeObserver(syncOffset).observe(bar);
    syncOffset();
    nav.addEventListener("click", (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const target = document.getElementById(a.hash.slice(1));
      if (!target) return;
      e.preventDefault();
      /* A collapsed accordion target is a dead landing (the customer scrolls to a
         closed «+» row); open it first, then travel. Keyed on the trigger's active
         CLASS (the visual truth) — vendor aria can lie for server-opened sections
         until luxCollapseStateSeed has run. */
      if (target.matches("collapsible-section")) {
        const trigger = target.querySelector(".wt-collapse__trigger");
        if (trigger && !trigger.classList.contains("wt-collapse__trigger--active")) trigger.click();
      }
      const reduce = window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches;
      syncOffset();
      target.scrollIntoView({ block: "start", behavior: reduce ? "auto" : "smooth" });
      history.replaceState(null, "", a.hash);
    });
    /* The section nearest ABOVE the bar line is the active one (order-independent:
       link order and page order need not match). */
    let ticking = false;
    const spy = () => {
      ticking = false;
      let active = -1;
      let bestTop = -Infinity;
      const spyLine = barOffset() + 16;
      targets.forEach((t, i) => {
        const top = t.getBoundingClientRect().top;
        if (top <= spyLine && top > bestTop) { bestTop = top; active = i; }
      });
      links.forEach((a, i) => {
        if (i === active) a.setAttribute("aria-current", "true");
        else a.removeAttribute("aria-current");
      });
    };
    addEventListener("scroll", () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(spy);
    }, { passive: true });
    spy();
  };

  /* Vendor bug shim: CollapsibleSection reads open state from ITS OWN data-open
     (isOpen(), base.js:623) but Liquid renders the initial state on the TRIGGER
     (active class + data-open). A server-opened section therefore boots with
     aria-expanded=false and an INVERTED first toggle. Seeding the element's
     data-open from the trigger's rendered class makes state, toggles, and aria
     agree (and fires the vendor's own aria/tabindex sync via observedAttributes). */
  const luxCollapseStateSeed = () =>
    customElements.whenDefined("collapsible-section").then(() => {
      document.querySelectorAll("collapsible-section").forEach((el) => {
        if (el.dataset.open) return;
        const t = el.querySelector(".wt-collapse__trigger");
        if (t) el.dataset.open = t.classList.contains("wt-collapse__trigger--active") ? "true" : "false";
      });
    });

  /* Mobile + tablet (<1200 = single-column layout): the description arrives OPEN
     from the template (right for desktop's two-column read) but on a phone it is a
     ~1900px text wall between the buy zone and the reviews (Tsoulfa 2026-07-18).
     Close it once at load through the vendor's own trigger, keyed on the rendered
     class (the visual truth). The text stays in the DOM for Google/AI; the sticky
     «Περιγραφή» tab and the row itself re-open it. The element sits ~2000px below
     the fold, so no visible shift. */
  const luxMobileDescCollapse = () => {
    if (!window.matchMedia || !matchMedia("(max-width: 1199px)").matches) return;
    customElements.whenDefined("collapsible-section").then(() => {
      const trigger = document.querySelector("#lux-description .wt-collapse__trigger");
      if (trigger && trigger.classList.contains("wt-collapse__trigger--active")) trigger.click();
    });
  };

  const init = () => {
    const gallery = document.querySelector("gallery-section[data-lux-selected]");
    if (gallery) applyScope(gallery.dataset.luxSelected.split("|||"));

    /* Wonder's variants.js calls gallery.filterSlides(options, media_id) on every
       variant resolve, on all devices. Its native version filters by an alt-HASHTAG
       convention Lux media don't use, so it kept every slide and, on mobile (where
       both swipers exist), re-appended ALL cloned slides and froze the wrapper height
       mid-swap (unfrozen only by a slide-0 img.onload that cached clones can skip):
       gallery showed every color and could stick shrunken. Shadowing the method per
       instance makes the lux scope filter the single gallery-filter path everywhere,
       with Wonder's own call timing; the old variantChange pubsub subscription is
       superseded by this hook and removed. */
    document.querySelectorAll("gallery-section").forEach((g) => {
      g.filterSlides = (options) => applyScope(options);
    });

    luxDelivery();
    luxPlans();
    luxFabric();
    luxCorner();
    luxChoiceGate();
    luxStickyTabs();
    luxCollapseStateSeed();
    luxMobileDescCollapse();
  };

  /* Lux plan selector engine (spec 2026-07-05). State survives Wonder's
     variant-change innerHTML swap of #price-{sectionId}: variants.js replaces the
     server HTML (default = cash x1), then publishes "variant-change"; we re-apply
     the stored selection and recompute. Events are DELEGATED on the stable
     .wt-product__price ancestor so the swap never kills listeners. */
  const luxPlans = () => {
    const wrap = document.querySelector("[data-lux-plans]");
    if (!wrap) return;
    const priceHost = wrap.closest(".wt-product__price") || wrap.parentElement;
    const sectionId = wrap.dataset.sectionId;
    const state = { mode: "cash", months: 1 };
    let planChoiceChanged = false;

    const readData = () => {
      const el = priceHost.querySelector("script[data-lux-plans-data]");
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch { return null; }
    };
    const fmtEUR = (cents) => {
      const whole = Math.round(cents) / 100;
      const opts = Number.isInteger(whole)
        ? { maximumFractionDigits: 0 }
        : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
      return "€" + whole.toLocaleString("el-GR", opts);
    };
    const qtyInput = document.querySelector('input[name="quantity"]');
    const qty = () => Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
    const currentVariant = () => {
      const data = readData();
      if (!data) return null;
      /* Variant id ONLY from the buy form (variants.js keeps that input current).
         Never query form[action*="/cart/add"] generically: the hidden payment_terms
         form also carries input[name=id] but is NOT re-rendered on variant change. */
      const atc = document.getElementById(`ProductSubmitButton-${sectionId}`);
      const buyForm = atc && atc.closest("form");
      const idInput = buyForm && buyForm.querySelector('input[name="id"]');
      const id = idInput ? parseInt(idInput.value, 10) : data.selectedId;
      return data.variants.find((v) => v.id === id) || data.variants[0];
    };

    const LUX_MIN_PRODUCT = 20000, LUX_MIN_MONTHLY = 4000, LUX_MAX_MONTHS = 12, LUX_MIN_MONTHS = 6;
    /* Offered terms (Tsoulfa 2026-07-18): 6, 9, 12 ONLY — "the others are redundant".
       Descending, so find() snaps an affordable cap DOWN to the nearest offered term. */
    const LUX_INST_TERMS = [12, 9, 6];
    const luxSnapTerm = (cap) => LUX_INST_TERMS.find((m) => m <= cap) || 0;
    /* Instalment eligibility + month cap (Tsoulfa final rule 2026-07-07): 20% upfront as a
       clean WHOLE euro (no cents on the deposit), remainder over 6-12 months (Tsoulfa
       2026-07-11: 4-5 removed, nobody used them) with the CENTS carried on the monthly,
       but NEVER a monthly under €40 and NO instalments on products under €200. maxMonths
       caps the term so the monthly stays >= €40; a term shorter than 6 months makes the
       plan ineligible outright (in practice: regular price below €300). deposit
       (whole-euro 20%) is reused verbatim by compute() and the teaser. */
    const planInfo = (v) => {
      if (!v) return { eligible: false, maxMonths: 0, reg: 0, deposit: 0 };
      const unitReg = Math.max(v.compare_at, v.price);
      const reg = unitReg * qty();
      const deposit = Math.round(reg * 0.2 / 100) * 100;
      const maxCalc = Math.min(LUX_MAX_MONTHS, Math.floor((reg - deposit) / LUX_MIN_MONTHLY));
      const snapped = luxSnapTerm(maxCalc);
      const eligible = unitReg >= LUX_MIN_PRODUCT && snapped >= LUX_MIN_MONTHS;
      const maxMonths = eligible ? snapped : 0;
      return { eligible, maxMonths, reg, deposit };
    };

    const compute = () => {
      const v = currentVariant();
      if (!v) return null;
      const q = qty();
      const cash = v.price * q;
      const reg = Math.max(v.compare_at, v.price) * q;
      if (state.mode === "cash") {
        /* Short split (Tsoulfa final rule 2026-07-07): cash price / n, cents on the monthly,
           floored so n×monthly never exceeds the price (never overcharge a cent). */
        const n = state.months;
        return { total: cash, n, monthly: Math.floor(cash / n), deposit: 0 };
      }
      /* Instalment split (Tsoulfa final rule 2026-07-07): 20% deposit as a clean WHOLE euro
         (no cents upfront); the CENTS ride on the monthly = floor((reg−deposit)/n). Floor,
         never round up, so deposit + n×monthly never EXCEEDS reg (the customer is never charged
         an extra cent). On terms that don't divide evenly it can land a few cents short of the
         total, and that is fine (Tsoulfa: "if short by a cent, forget it"). Total shown = reg. */
      const deposit = Math.round(reg * 0.2 / 100) * 100;
      const n = state.months;
      const monthly = Math.floor((reg - deposit) / n);
      return { total: reg, n, monthly, deposit };
    };

    const t = (key, repl) => {
      let s = (window.luxPlanStrings && window.luxPlanStrings[key]) || "";
      Object.entries(repl || {}).forEach(([k, val]) => { s = s.replace(`{{ ${k} }}`, val); });
      return s;
    };

    const render = () => {
      const info = planInfo(currentVariant());
      const instCard = priceHost.querySelector('[data-lux-card="inst"]');
      if (instCard) instCard.hidden = !info.eligible;
      if (state.mode === "inst") {
        if (!info.eligible) { state.mode = "cash"; state.months = 1; planChoiceChanged = false; }
        else if (!LUX_INST_TERMS.includes(state.months) || state.months > info.maxMonths) state.months = info.maxMonths;
      }
      const p = compute();
      if (!p) return;
      priceHost.querySelectorAll("[data-lux-card]").forEach((card) => {
        const active = card.dataset.luxCard === state.mode;
        card.classList.toggle("lux-plans__card--active", active);
        const input = card.querySelector("input");
        if (input) input.checked = active;
      });
      priceHost.querySelectorAll("[data-lux-months]").forEach((fs) => {
        fs.hidden = fs.dataset.luxMonths !== state.mode;
        const isInst = fs.dataset.luxMonths === "inst";
        fs.querySelectorAll(".lux-plans__pill").forEach((pill) => {
          const input = pill.querySelector("input");
          const val = parseInt(input.value, 10);
          if (isInst && val > info.maxMonths) {
            pill.hidden = true;
            input.checked = false;
            pill.classList.remove("lux-plans__pill--active");
            return;
          }
          const on = val === state.months && !fs.hidden;
          pill.classList.toggle("lux-plans__pill--active", on);
          input.checked = on;
          if (on && pill.hidden) pill.hidden = false;
        });
      });
      /* Instalment card teaser: shows the SELECTED months' monthly when in inst
         mode, else the 12-month best case (smallest honest monthly). */
      const teaser = priceHost.querySelector("[data-lux-inst-amount]");
      if (teaser && info.eligible && info.maxMonths > 0) {
        const tN = state.mode === "inst" ? state.months : info.maxMonths;
        teaser.textContent = fmtEUR(Math.floor((info.reg - info.deposit) / tN));
      }
      /* Receipt rows (Tsoulfa 2026-07-11): the one-line math read as clutter;
         three aligned rows read instantly. Sticky bar keeps the single line. */
      const mathBox = priceHost.querySelector(".lux-plans__math");
      if (mathBox) {
        const isSingle = state.mode === "cash" && p.n === 1;
        mathBox.hidden = isSingle;
        if (!isSingle) {
          const depRow = mathBox.querySelector("[data-lux-row-deposit]");
          if (depRow) {
            depRow.hidden = p.deposit <= 0;
            const dv = depRow.querySelector("[data-lux-val-deposit]");
            if (dv) dv.textContent = fmtEUR(p.deposit);
          }
          const mv = mathBox.querySelector("[data-lux-val-monthly]");
          if (mv) mv.textContent = p.n + " × " + fmtEUR(p.monthly);
          const tv = mathBox.querySelector("[data-lux-val-total]");
          if (tv) tv.textContent = fmtEUR(p.total);
        }
      }
      syncCta(p);
    };

    /* One-button PDP (spec 2026-07-10): add-to-cart always renders; only the
       dynamic checkout buttons hide on multi-month plans (they would charge
       the cash price instantly). The application now starts from the CART. */
    const syncCta = (p) => {
      const isCheckout = state.mode === "cash" && state.months === 1;
      const atc = document.getElementById(`ProductSubmitButton-${sectionId}`);
      const form = atc && atc.closest("form");
      const payBtn = form && form.parentElement.querySelector(".shopify-payment-button");
      if (payBtn) payBtn.hidden = !isCheckout;
      const hint = priceHost.querySelector("[data-lux-cart-hint]");
      if (hint) hint.hidden = isCheckout;
      mirrorSticky(p, !isCheckout);
    };

    const mirrorSticky = (p, showPlan) => {
      const stickyPrice = document.getElementById(`sticky-price-${sectionId}`);
      if (!stickyPrice) return;
      let tag = stickyPrice.querySelector("[data-lux-sticky-plan]");
      if (!tag) {
        tag = document.createElement("span");
        tag.dataset.luxStickyPlan = "";
        tag.className = "lux-plans__total";
        stickyPrice.appendChild(tag);
      }
      /* Mode-locked price story (Tsoulfa 2026-07-11): in plan mode the sticky
         hides the native (cash) price via .lux-sticky--plan and leads with the
         plan's own total, so no discounted number contradicts the plan. */
      stickyPrice.classList.toggle("lux-sticky--plan", showPlan);
      /* Two-tier price (Tsoulfa 2026-07-12): total is the headline, the deposit/monthly
         breakdown is a quiet caption. On the mobile stacked bar CSS makes them two tiers;
         on desktop they read inline as one line ("total · breakdown") via a ::before dot. */
      tag.textContent = "";
      if (showPlan) {
        const totalEl = document.createElement("span");
        totalEl.className = "lux-sticky-plan__total";
        totalEl.textContent = t("total_line", { total: fmtEUR(p.total) });
        const breakEl = document.createElement("span");
        breakEl.className = "lux-sticky-plan__break";
        breakEl.textContent = p.deposit > 0
          ? t("deposit_line", { deposit: fmtEUR(p.deposit), count: p.n, amount: fmtEUR(p.monthly) })
          : t("payments_line", { count: p.n, amount: fmtEUR(p.monthly) });
        tag.append(totalEl, breakEl);
      }
    };

    /* Selecting an already checked cash card is still an explicit choice.
       Native label/input clicks also cover keyboard activation. */
    priceHost.addEventListener("click", (e) => {
      if (e.target.closest("[data-lux-card], [data-lux-months] .lux-plans__pill")) planChoiceChanged = true;
    });
    priceHost.addEventListener("change", (e) => {
      const input = e.target;
      if (input.name === `lux-plan-mode-${sectionId}`) {
        state.mode = input.value;
        if (state.mode === "cash") state.months = 1;
        else state.months = planInfo(currentVariant()).maxMonths || LUX_MIN_MONTHS;
      } else if (input.name === `lux-plan-months-${sectionId}`) {
        state.months = parseInt(input.value, 10);
      } else return;
      planChoiceChanged = true;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "lux_plan_selected", plan_type: state.mode, months: state.months });
      render();
    });
    /* Only a new, explicit PDP choice replaces the basket's plan. A fresh PDP
       defaults to cash, but that default must not erase a plan chosen in the
       cart. Consume the choice once so later adds keep any cart-side edits.
       This remains a cosmetic hint; blocked storage leaves the cart safe. */
    document.addEventListener("click", (e) => {
      if (!e.target.closest(`#ProductSubmitButton-${sectionId}, .wt-product__sticky-buy__button`)) return;
      if (!planChoiceChanged) return;
      planChoiceChanged = false;
      const isCheckout = state.mode === "cash" && state.months === 1;
      try {
        if (isCheckout) sessionStorage.removeItem("luxCartPlan");
        else sessionStorage.setItem("luxCartPlan", JSON.stringify({ mode: state.mode, months: state.months }));
      } catch (err) { /* storage blocked: cart defaults to cash */ }
    });
    if (qtyInput) qtyInput.addEventListener("change", render);
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      subscribe(PUB_SUB_EVENTS.variantChange, () => render());
      subscribe(PUB_SUB_EVENTS.quantityUpdate, () => render());
    }
    render();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
